import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { GlassCard } from '@/components/GlassCard';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Heart, MessageCircle, ImagePlus, Send, Plus, MapPin, Radio, Sparkles, X, Share2, Megaphone, Pin, MoreHorizontal, Pencil, Trash2, Check, Film, RefreshCw, GraduationCap, ChevronRight } from 'lucide-react';
import { WelcomeTour } from '@/components/WelcomeTour';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { EditPostDialog } from '@/components/EditPostDialog';
import { ImageLightbox } from '@/components/ImageLightbox';
import { uploadVideoFile } from '@/lib/videoUpload';
import { attachVideoSource } from '@/lib/hls';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { fileToDataUrl, timeAgo, cn } from '@/lib/utils';
import { uploadImage } from '@/lib/mediaUpload';
import { EmptyState, FullPageLoader } from '@/components/Loaders';
import { getCurrentSlot } from '@/components/TimeOfDayBackground';
import { ShareAppDialog } from '@/components/ShareAppDialog';
import { LiveViewerDialog } from '@/components/LiveViewerDialog';
import { PushBellButton } from '@/components/PushBellButton';
import { ReplaysRail } from '@/components/ReplaysRail';
import { Seo } from '@/components/Seo';
import { ThirrstyBadge } from '@/components/ThirrstyBadge';

function StoryBar({ stories, onAdd, onView }) {
  return (
    <div className="-mx-4 px-4 py-3" data-testid="timeline-stories-scroll">
      <div className="flex gap-3 overflow-x-auto no-scrollbar">
        <button
          onClick={onAdd}
          className="shrink-0 flex flex-col items-center gap-1"
          data-testid="timeline-add-story-button"
        >
          <div className="relative h-16 w-16 rounded-full border-2 border-dashed border-white/30 bg-white/5 grid place-items-center hover:border-[#F6B24A]/70 transition-colors">
            <Plus className="h-5 w-5 text-white/70" />
          </div>
          <span className="text-[11px] text-white/70">Your story</span>
        </button>
        {stories.map((s) => (
          <button key={s.id} onClick={() => onView(s)} className="shrink-0 flex flex-col items-center gap-1" data-testid={`timeline-story-${s.id}`}>
            <div className="relative h-16 w-16 rounded-full p-[2px] bg-gradient-to-tr from-[#F6B24A] to-[#2DE2E6]">
              {s.video_url ? (
                <video
                  src={s.video_url}
                  poster={s.poster_url || undefined}
                  className="h-full w-full rounded-full border-2 border-[#07070A] object-cover"
                  playsInline
                  muted
                  preload="metadata"
                  data-testid={`timeline-story-video-${s.id}`}
                />
              ) : (
                <img
                  src={s.image_data_url}
                  alt={s.user_display_name}
                  className="h-full w-full rounded-full border-2 border-[#07070A] object-cover"
                />
              )}
              {s.video_url && (
                <span className="absolute -bottom-0.5 -right-0.5 grid h-5 w-5 place-items-center rounded-full bg-[#F6B24A] text-[#1A1208] text-[10px] font-bold border-2 border-[#07070A]" data-testid={`timeline-story-video-badge-${s.id}`}>▶</span>
              )}
            </div>
            <span className="text-[11px] text-white/70 max-w-[64px] truncate">{s.user_display_name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Composer({ user, onPosted }) {
  const [text, setText] = useState('');
  const [image, setImage] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [progress, setProgress] = useState(0);
  const [posting, setPosting] = useState(false);
  const [sponsored, setSponsored] = useState(false);
  const [myVenue, setMyVenue] = useState(null);
  const fileRef = useRef();
  const videoRef = useRef();
  const canSponsor = user?.role === 'admin' || Boolean(myVenue);

  const resetMedia = () => {
    setImage(null);
    setVideoFile(null);
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setVideoPreview(null);
  };

  useEffect(() => {
    if (user?.role !== 'venue_owner' && user?.role !== 'admin') return;
    api.get('/subscriptions/me').then((r) => {
      const active = (r.data || []).find(
        (it) => it.subscription && ['active', 'trial'].includes(it.subscription.status) && it.venue
      );
      if (active) setMyVenue(active.venue);
    }).catch(() => {});
  }, [user?.role]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    resetMedia();
    if (f.type.startsWith('video/')) {
      if (f.size > 100 * 1024 * 1024) {
        toast.error('Video too large — 100 MB max');
        return;
      }
      setVideoFile(f);
      setVideoPreview(URL.createObjectURL(f));
    } else {
      try {
        const dataUrl = await fileToDataUrl(f);
        setImage(dataUrl);
      } catch {
        toast.error('Failed to read image');
      }
    }
    e.target.value = '';
  };

  const onVideoFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = '';
    if (!f.type.startsWith('video/')) {
      toast.error("That file isn't a video — please select a video");
      return;
    }
    if (f.size > 100 * 1024 * 1024) {
      toast.error('Video too large — 100 MB max');
      return;
    }
    resetMedia();
    setVideoFile(f);
    setVideoPreview(URL.createObjectURL(f));
  };

  const uploadVideo = () => uploadVideoFile(videoFile, setProgress);

  const submit = async () => {
    if (!text.trim() && !image && !videoFile) {
      toast.error('Add a caption, photo or video');
      return;
    }
    setPosting(true);
    setProgress(0);
    try {
      let videoUrl = null;
      let posterUrl = null;
      if (videoFile) ({ video_url: videoUrl, poster_url: posterUrl } = await uploadVideo());
      const imageUrl = await uploadImage(image);
      const r = await api.post('/posts', {
        text: text.trim(),
        image_data_url: imageUrl,
        video_url: videoUrl,
        poster_url: posterUrl,
        is_sponsored: sponsored,
        venue_id: sponsored ? myVenue?.id : undefined,
      });
      toast.success(sponsored ? 'Ad posted to timeline' : 'Posted to feed');
      setText('');
      resetMedia();
      setSponsored(false);
      onPosted?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not post');
    } finally {
      setPosting(false);
      setProgress(0);
    }
  };

  return (
    <GlassCard className="p-4">
      <div className="flex items-start gap-3">
        <Avatar className="h-10 w-10">
          <AvatarImage src={user?.avatar_url} />
          <AvatarFallback className="bg-[#F6B24A]/30 text-[#F6B24A]">{(user?.display_name || 'U').slice(0, 1)}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <Textarea
            value={text} onChange={(e) => setText(e.target.value)}
            placeholder="What's the vibe tonight?"
            className="min-h-[60px] resize-none bg-transparent border-0 px-0 text-white placeholder:text-white/40 focus-visible:ring-0"
            data-testid="timeline-compose-textarea"
          />
          {image && (
            <div className="relative mt-2 overflow-hidden rounded-xl border border-white/10">
              <img src={image} alt="Selected" className="max-h-72 w-full object-cover" />
              <button
                onClick={resetMedia}
                className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
                data-testid="timeline-compose-remove-image-button"
              ><X className="h-4 w-4" /></button>
            </div>
          )}
          {videoPreview && (
            <div className="relative mt-2 overflow-hidden rounded-xl border border-white/10">
              <video src={videoPreview} controls playsInline className="max-h-72 w-full bg-black" data-testid="timeline-compose-video-preview" />
              <button
                onClick={resetMedia}
                className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
                data-testid="timeline-compose-remove-video-button"
              ><X className="h-4 w-4" /></button>
            </div>
          )}
          <div className={cn('mt-3 grid items-center gap-2', canSponsor ? 'grid-cols-4' : 'grid-cols-3')}>
            <Button
              type="button" variant="outline"
              onClick={() => fileRef.current?.click()}
              className="w-full rounded-xl border-white/12 bg-white/5 px-2 text-white hover:bg-white/10"
              data-testid="timeline-compose-add-image-button"
            >
              <ImagePlus className="mr-1 h-4 w-4 shrink-0" /> Photo
            </Button>
            <Button
              type="button" variant="outline"
              onClick={() => videoRef.current?.click()}
              className="w-full rounded-xl border-white/12 bg-white/5 px-2 text-white hover:bg-white/10"
              data-testid="timeline-compose-add-video-button"
            >
              <Film className="mr-1 h-4 w-4 shrink-0" /> Video
            </Button>
            {canSponsor && (
              <Button
                type="button" variant="outline"
                onClick={() => setSponsored((s) => !s)}
                className={cn(
                  'w-full rounded-xl border-white/12 bg-white/5 px-2 text-white hover:bg-white/10',
                  sponsored && 'border-[#F6B24A]/60 bg-[#F6B24A]/15 text-[#F6B24A] hover:bg-[#F6B24A]/25'
                )}
                data-testid="timeline-compose-sponsored-toggle"
              >
                <Megaphone className="mr-1 h-4 w-4 shrink-0" /> {sponsored ? 'Ad ✓' : 'Ad'}
              </Button>
            )}
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="timeline-compose-file-input" />
            <input ref={videoRef} type="file" hidden onChange={onVideoFile} data-testid="timeline-compose-video-input" />
            <Button
              onClick={submit} disabled={posting}
              className="w-full rounded-xl bg-[#F6B24A] px-2 text-[#1A1208] hover:bg-[#FFC065] font-semibold"
              data-testid="timeline-compose-submit-button"
            >
              <Send className="mr-1 h-4 w-4 shrink-0" /> {posting && videoFile ? `${progress}%` : posting ? 'Posting…' : 'Post'}
            </Button>
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

let activeFeedVideo = null;

function FeedVideo({ src, poster, testId }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let detach = null;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !detach) detach = attachVideoSource(el, src);
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          if (activeFeedVideo && activeFeedVideo !== el && !activeFeedVideo.paused) {
            activeFeedVideo.pause();
          }
          activeFeedVideo = el;
          el.play().catch(() => {});
        } else {
          if (!el.paused) el.pause();
          if (activeFeedVideo === el) activeFeedVideo = null;
        }
      },
      { threshold: [0, 0.6] }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      if (detach) detach();
      if (activeFeedVideo === el) activeFeedVideo = null;
    };
  }, [src]);
  return (
    <video
      ref={ref}
      poster={poster || undefined}
      className="w-full max-h-[560px] bg-black object-cover"
      playsInline
      muted
      loop
      preload="none"
      controls
      data-testid={testId}
    />
  );
}

function StoryVideo({ src, poster }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const detach = attachVideoSource(el, src);
    el.play().catch(() => {});
    return detach;
  }, [src]);
  return (
    <video
      ref={ref}
      poster={poster || undefined}
      className="w-full max-h-[80vh] bg-black object-contain"
      playsInline
      autoPlay
      loop
      controls
      data-testid="story-viewer-video"
    />
  );
}

function PostCard({ post, currentUserId, isAdmin, onLike, onComment, onEdit, onDelete, pinned = false }) {
  const nav = useNavigate();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const liked = (post.likes || []).includes(currentUserId);
  const canManage = currentUserId && (post.user_id === currentUserId || isAdmin);
  return (
    <GlassCard
      className={cn('th-float-in overflow-hidden', pinned && 'border-[#F6B24A]/40 ring-1 ring-[#F6B24A]/25')}
      data-testid={`timeline-post-${post.id}`}
    >
      {pinned && (
        <div className="flex items-center gap-1.5 border-b border-[#F6B24A]/20 bg-[#F6B24A]/10 px-4 py-1.5" data-testid={`timeline-pinned-ad-label-${post.id}`}>
          <Pin className="h-3 w-3 text-[#F6B24A]" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#F6B24A]">Pinned · Sponsored</span>
        </div>
      )}
      <div className="flex items-center gap-3 p-4">
        <Link to={`/user/${post.user_id}`} data-testid={`post-author-avatar-${post.id}`}>
          <Avatar className="h-10 w-10">
            <AvatarImage src={post.user_avatar_url} />
            <AvatarFallback className="bg-[#F6B24A]/30 text-[#F6B24A]">{(post.user_display_name || 'U').slice(0, 1)}</AvatarFallback>
          </Avatar>
        </Link>
        <div className="flex-1">
          <Link to={`/user/${post.user_id}`} className="text-sm font-semibold text-white hover:text-[#F6B24A]" data-testid={`post-author-name-${post.id}`}>
            {post.user_display_name}
          </Link>
          <p className="text-xs text-white/60">{timeAgo(post.created_at)} ago{post.edited_at ? ' · edited' : ''}{post.venue_name ? ` · ${post.venue_name}` : ''}</p>
        </div>
        <div className="flex items-center gap-1">
          <div className="flex flex-col items-end gap-1">
            {post.venue_is_thirrsty && <ThirrstyBadge size="xs" testId={`post-thirrsty-badge-${post.id}`} />}
            {post.is_sponsored && (
              <Badge className="bg-white/10 text-white/70 border border-white/10" data-testid={`post-sponsored-badge-${post.id}`}>Sponsored</Badge>
            )}
          </div>
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-white/60 hover:text-white hover:bg-white/10" data-testid={`post-menu-button-${post.id}`}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#0B0C10] border-white/10 text-white">
                <DropdownMenuItem onClick={() => onEdit(post)} className="cursor-pointer focus:bg-white/10 focus:text-white" data-testid={`post-edit-item-${post.id}`}>
                  <Pencil className="mr-2 h-4 w-4" /> Edit post
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDelete(post)} className="cursor-pointer text-red-400 focus:bg-red-500/10 focus:text-red-400" data-testid={`post-delete-item-${post.id}`}>
                  <Trash2 className="mr-2 h-4 w-4" /> Delete post
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      {post.text && <p className="px-4 pb-3 text-sm text-white/90 whitespace-pre-wrap">{post.text}</p>}
      {post.video_url ? (
        <FeedVideo src={post.video_url} poster={post.poster_url} testId={`timeline-post-video-${post.id}`} />
      ) : post.image_data_url && (
        <img
          src={post.image_data_url}
          alt="post"
          onClick={() => setLightboxOpen(true)}
          className="w-full max-h-[520px] cursor-zoom-in object-cover"
          data-testid={`timeline-post-image-${post.id}`}
        />
      )}
      {lightboxOpen && <ImageLightbox src={post.image_data_url} onClose={() => setLightboxOpen(false)} />}
      <div className="flex items-center gap-2 px-2 py-2 border-t border-white/10">
        <Button
          variant="ghost" onClick={() => onLike(post)}
          className={cn('rounded-full text-white/80 hover:text-white hover:bg-white/10', liked && 'text-red-400')}
          data-testid={`timeline-post-like-button-${post.id}`}
        >
          <Heart className={cn('mr-2 h-4 w-4', liked && 'fill-current')} />
          <span data-testid={`timeline-post-like-count-${post.id}`}>{(post.likes || []).length}</span>
        </Button>
        <Button
          variant="ghost" onClick={() => onComment(post)}
          className="rounded-full text-white/80 hover:text-white hover:bg-white/10"
          data-testid={`timeline-post-comment-button-${post.id}`}
        >
          <MessageCircle className="mr-2 h-4 w-4" /> {post.comments_count || 0}
        </Button>
        {post.venue_id && (
          <Button
            variant="ghost"
            onClick={() => nav(`/venue/${post.venue_id}`)}
            className="ml-auto rounded-full text-[#F6B24A] hover:bg-[#F6B24A]/10"
            data-testid={`timeline-post-venue-link-${post.id}`}
          >
            <MapPin className="mr-1 h-4 w-4" /> Venue
          </Button>
        )}
      </div>
    </GlassCard>
  );
}

function CommentSheet({ open, post, onClose, currentUser }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    if (!post) return;
    setLoading(true);
    api.get(`/posts/${post.id}/comments`).then((r) => setComments(r.data)).finally(() => setLoading(false));
  }, [post]);

  const submit = async () => {
    if (!text.trim()) return;
    try {
      const r = await api.post(`/posts/${post.id}/comments`, { text: text.trim() });
      setComments((c) => [...c, r.data]);
      setText('');
    } catch {
      toast.error('Could not post comment');
    }
  };

  const saveEdit = async (id) => {
    if (!editText.trim()) return;
    try {
      const r = await api.patch(`/comments/${id}`, { text: editText.trim() });
      setComments((c) => c.map((x) => (x.id === id ? r.data : x)));
      setEditingId(null);
    } catch {
      toast.error('Could not update comment');
    }
  };

  const removeComment = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      setComments((c) => c.filter((x) => x.id !== id));
      toast.success('Comment deleted');
    } catch {
      toast.error('Could not delete comment');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-[#0B0C10] border border-white/10 text-white" data-testid="comments-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Comments</DialogTitle>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto space-y-3 pr-1">
          {loading && <p className="text-sm text-white/60">Loading…</p>}
          {!loading && comments.length === 0 && <p className="text-sm text-white/60">Be the first to comment.</p>}
          {comments.map((c) => {
            const own = currentUser && (c.user_id === currentUser.id || currentUser.role === 'admin');
            return (
              <div key={c.id} className="flex gap-2" data-testid={`comment-${c.id}`}>
                <Avatar className="h-8 w-8">
                  <AvatarImage src={c.user_avatar_url} />
                  <AvatarFallback className="bg-[#F6B24A]/30 text-[#F6B24A] text-xs">{(c.user_display_name || 'U').slice(0, 1)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 rounded-xl bg-white/5 p-2 px-3">
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-semibold text-white">{c.user_display_name}</p>
                    {c.edited_at && <span className="text-[10px] text-white/40">edited</span>}
                    {own && editingId !== c.id && (
                      <span className="ml-auto flex gap-1">
                        <button
                          onClick={() => { setEditingId(c.id); setEditText(c.text); }}
                          className="rounded p-1 text-white/40 hover:text-white"
                          data-testid={`comment-edit-button-${c.id}`}
                        ><Pencil className="h-3 w-3" /></button>
                        <button
                          onClick={() => removeComment(c.id)}
                          className="rounded p-1 text-white/40 hover:text-red-400"
                          data-testid={`comment-delete-button-${c.id}`}
                        ><Trash2 className="h-3 w-3" /></button>
                      </span>
                    )}
                  </div>
                  {editingId === c.id ? (
                    <div className="mt-1 flex items-center gap-2">
                      <Textarea
                        value={editText} onChange={(e) => setEditText(e.target.value)}
                        className="min-h-[36px] flex-1 resize-none bg-white/5 border-white/10 text-sm text-white"
                        data-testid={`comment-edit-input-${c.id}`}
                      />
                      <button onClick={() => saveEdit(c.id)} className="rounded p-1 text-[#F6B24A] hover:text-[#FFC065]" data-testid={`comment-edit-save-${c.id}`}><Check className="h-4 w-4" /></button>
                      <button onClick={() => setEditingId(null)} className="rounded p-1 text-white/40 hover:text-white"><X className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <p className="text-sm text-white/85">{c.text}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter className="flex-row gap-2 sm:flex-row">
          <Textarea
            value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Add a comment"
            className="min-h-[44px] flex-1 resize-none bg-white/5 border-white/10 text-white placeholder:text-white/40"
            data-testid="comment-input"
          />
          <Button onClick={submit} className="rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065]" data-testid="comment-submit-button">
            <Send className="h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StoryViewerDialog({ story, onClose }) {
  if (!story) return null;
  const shareStory = async () => {
    const url = `${window.location.origin}/story/${story.id}`;
    const payload = { title: 'Thirrsty Story', text: `Check out ${story.user_display_name}'s story on Thirrsty 🍸`, url };
    try {
      if (navigator.share) {
        await navigator.share(payload);
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Story link copied — send it to your friends!');
      }
    } catch (e) {
      if (e?.name !== 'AbortError') {
        try { await navigator.clipboard.writeText(url); toast.success('Story link copied!'); }
        catch { toast.error('Could not share'); }
      }
    }
  };
  return (
    <Dialog open={!!story} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-black border-white/10 p-0 overflow-hidden" data-testid="story-viewer-dialog">
        <DialogTitle className="sr-only">Story viewer</DialogTitle>
        <div className="relative">
          {story.video_url ? (
            <StoryVideo src={story.video_url} poster={story.poster_url} />
          ) : (
            <img src={story.image_data_url} alt="story" className="w-full max-h-[80vh] object-cover" />
          )}
          <div className="absolute left-3 top-3 flex items-center gap-2 rounded-full bg-black/50 px-2 py-1 backdrop-blur-sm">
            <Avatar className="h-7 w-7">
              <AvatarImage src={story.user_avatar_url} />
              <AvatarFallback className="bg-[#F6B24A]/30 text-[#F6B24A] text-xs">{(story.user_display_name || 'U').slice(0, 1)}</AvatarFallback>
            </Avatar>
            <span className="text-xs font-semibold text-white">{story.user_display_name}</span>
            <span className="text-[10px] text-white/60">{timeAgo(story.created_at)} ago</span>
          </div>
          <button
            type="button"
            onClick={shareStory}
            className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-black/50 text-white backdrop-blur-sm hover:bg-black/70"
            aria-label="Share story"
            data-testid="story-share-button"
          >
            <Share2 className="h-4 w-4" />
          </button>
          {story.caption && (
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-4">
              <p className="text-sm text-white/90">{story.caption}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StoryUploadDialog({ open, onOpenChange, onCreated }) {
  const [image, setImage] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [posting, setPosting] = useState(false);
  const [progress, setProgress] = useState(0);
  const ref = useRef();
  const videoInputRef = useRef();

  const resetMedia = () => {
    setImage(null);
    setVideoFile(null);
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setVideoPreview(null);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    resetMedia();
    if (f.type.startsWith('video/')) {
      if (f.size > 100 * 1024 * 1024) {
        toast.error('Video too large — 100 MB max');
        return;
      }
      setVideoFile(f);
      setVideoPreview(URL.createObjectURL(f));
    } else {
      const dataUrl = await fileToDataUrl(f, 1200, 0.78);
      setImage(dataUrl);
    }
    e.target.value = '';
  };

  const onVideoFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = '';
    if (!f.type.startsWith('video/')) {
      toast.error("That file isn't a video — please select a video");
      return;
    }
    if (f.size > 100 * 1024 * 1024) {
      toast.error('Video too large — 100 MB max');
      return;
    }
    resetMedia();
    setVideoFile(f);
    setVideoPreview(URL.createObjectURL(f));
  };

  const uploadVideo = () => uploadVideoFile(videoFile, setProgress);

  const submit = async () => {
    if (!image && !videoFile) {
      toast.error('Pick a photo or video');
      return;
    }
    setPosting(true);
    setProgress(0);
    try {
      let videoUrl = null;
      let posterUrl = null;
      if (videoFile) ({ video_url: videoUrl, poster_url: posterUrl } = await uploadVideo());
      const imageUrl = await uploadImage(image);
      const r = await api.post('/stories', {
        image_data_url: imageUrl,
        video_url: videoUrl,
        poster_url: posterUrl,
        caption: caption || null,
      });
      toast.success('Story posted (expires in 24h)');
      resetMedia();
      setCaption('');
      onCreated?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not post story');
    } finally {
      setPosting(false);
      setProgress(0);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#0B0C10] border border-white/10 text-white" data-testid="story-upload-dialog">
        <DialogHeader><DialogTitle className="text-white">Post a 24h Story</DialogTitle>
          <DialogDescription className="sr-only">Share a photo or video that disappears after 24 hours</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!image && !videoPreview ? (
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => ref.current?.click()}
                className="flex h-40 w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-white/20 bg-white/5 text-white/70 hover:border-[#F6B24A]/60"
                data-testid="story-pick-image-button"
              >
                <ImagePlus className="h-6 w-6" />
                <span className="text-sm">Photo Library</span>
              </button>
              <button
                onClick={() => videoInputRef.current?.click()}
                className="flex h-40 w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-white/20 bg-white/5 text-white/70 hover:border-[#F6B24A]/60"
                data-testid="story-pick-video-button"
              >
                <Film className="h-6 w-6" />
                <span className="text-sm">Video Library</span>
                <span className="text-[11px] text-white/40">Up to 100 MB</span>
              </button>
            </div>
          ) : image ? (
            <div className="relative overflow-hidden rounded-xl">
              <img src={image} alt="" className="w-full object-cover" />
              <button onClick={resetMedia} className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80" data-testid="story-remove-media"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <div className="relative overflow-hidden rounded-xl bg-black" data-testid="story-video-preview">
              <video src={videoPreview} controls playsInline className="max-h-72 w-full" />
              <button onClick={resetMedia} className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80" data-testid="story-remove-media"><X className="h-4 w-4" /></button>
            </div>
          )}
          <input ref={ref} type="file" accept="image/*" hidden onChange={onFile} />
          <input ref={videoInputRef} type="file" hidden onChange={onVideoFile} data-testid="story-video-input" />
          <Textarea
            value={caption} onChange={(e) => setCaption(e.target.value)}
            placeholder="Add a caption (optional)"
            className="min-h-[44px] resize-none bg-white/5 border-white/10 text-white placeholder:text-white/40"
          />
          <Button onClick={submit} disabled={posting} className="w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065]" data-testid="story-submit-button">
            {posting ? (videoFile ? `Uploading… ${progress}%` : 'Posting…') : 'Post Story'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LiveStrip({ liveList }) {
  const nav = useNavigate();
  const [viewing, setViewing] = useState(null);
  if (!liveList?.length) return null;
  return (
    <>
      <GlassCard className="p-3">
        <div className="flex items-center gap-2 px-1">
          <span className="inline-flex h-2 w-2 rounded-full th-live-dot" />
          <p className="text-xs font-semibold uppercase tracking-wider text-red-300">Live now</p>
        </div>
        <div className="mt-2 flex gap-3 overflow-x-auto no-scrollbar">
          {liveList.map((l, i) => {
            const hasVideo = !!l.live_room;
            const onClick = () => {
              if (hasVideo) {
                setViewing({
                  room: l.live_room,
                  hostName: l.user.display_name,
                  venueName: l.venue?.name,
                });
              } else if (l.venue) {
                nav(`/venue/${l.user.live_at_venue_id}`);
              }
            };
            return (
              <button
                key={i}
                onClick={onClick}
                className={cn(
                  'shrink-0 flex items-center gap-2 rounded-full border px-3 py-1.5',
                  hasVideo
                    ? 'border-red-500/60 bg-red-500/20 shadow-[0_0_18px_-6px_rgba(239,68,68,0.6)]'
                    : 'border-red-500/30 bg-red-500/10'
                )}
                data-testid={`home-live-pill-${i}`}
              >
                <Avatar className="h-6 w-6">
                  <AvatarImage src={l.user.avatar_url} />
                  <AvatarFallback className="bg-red-500/40 text-white text-xs">{(l.user.display_name || 'U').slice(0, 1)}</AvatarFallback>
                </Avatar>
                <span className="text-xs text-red-100">
                  {l.user.display_name} · {l.venue?.name || 'live'}
                </span>
                {hasVideo && (
                  <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-red-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white" data-testid={`home-live-watch-badge-${i}`}>
                    Watch
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </GlassCard>
      <LiveViewerDialog
        open={!!viewing}
        room={viewing?.room}
        hostName={viewing?.hostName}
        venueName={viewing?.venueName}
        onClose={() => setViewing(null)}
      />
    </>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [posts, setPosts] = useState([]);
  const [stories, setStories] = useState([]);
  const [liveList, setLiveList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentPost, setCommentPost] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [deletingPost, setDeletingPost] = useState(null);

  const confirmDelete = async () => {
    if (!deletingPost) return;
    try {
      await api.delete(`/posts/${deletingPost.id}`);
      setPosts((prev) => prev.filter((p) => p.id !== deletingPost.id));
      toast.success('Post deleted');
    } catch {
      toast.error('Could not delete post');
    }
    setDeletingPost(null);
  };
  const [storyView, setStoryView] = useState(null);
  const [storyUploadOpen, setStoryUploadOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = React.useRef(null);
  const PAGE = 20;

  const load = async () => {
    setLoading(true);
    try {
      const fetchCount = Math.max(posts.length, PAGE);
      const [p, s, l] = await Promise.all([
        api.get(`/posts?limit=${fetchCount}&offset=0`),
        api.get('/stories'),
        api.get('/golive/active'),
      ]);
      setPosts(p.data);
      setStories(s.data);
      setLiveList(l.data);
      setHasMore(p.data.length >= fetchCount);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const refreshingRef = useRef(false);
  const refreshFeed = React.useCallback(async () => {
    if (refreshingRef.current || document.visibilityState === 'hidden') return;
    refreshingRef.current = true;
    try {
      const [p, s, l] = await Promise.all([
        api.get(`/posts?limit=${PAGE}&offset=0`),
        api.get('/stories'),
        api.get('/golive/active'),
      ]);
      setPosts((prev) => {
        const fresh = p.data || [];
        const freshIds = new Set(fresh.map((x) => x.id));
        return [...fresh, ...prev.filter((x) => !freshIds.has(x.id))];
      });
      setStories(s.data);
      setLiveList(l.data);
    } catch {
      // silent: background refresh must never disturb the feed
    } finally {
      refreshingRef.current = false;
    }
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshFeed();
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(refreshFeed, 15000);
    return () => {
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
    };
  }, [refreshFeed]);

  const [pullY, setPullY] = useState(0);
  const [tourOpen, setTourOpen] = useState(false);
  const [ptrBusy, setPtrBusy] = useState(false);
  const ptrRef = useRef({ startY: 0, active: false, dist: 0 });
  const onPtrStart = (e) => {
    if (window.scrollY <= 0) {
      ptrRef.current = { startY: e.touches[0].clientY, active: true, dist: 0 };
    }
  };
  const onPtrMove = (e) => {
    if (!ptrRef.current.active) return;
    const dy = e.touches[0].clientY - ptrRef.current.startY;
    if (dy > 0 && window.scrollY <= 0) {
      const dist = Math.min(90, dy * 0.45);
      ptrRef.current.dist = dist;
      setPullY(dist);
    }
  };
  const onPtrEnd = async () => {
    if (!ptrRef.current.active) return;
    const shouldRefresh = ptrRef.current.dist >= 55;
    ptrRef.current.active = false;
    ptrRef.current.dist = 0;
    if (shouldRefresh) {
      setPtrBusy(true);
      setPullY(55);
      await refreshFeed();
      setPtrBusy(false);
    }
    setPullY(0);
  };


  const loadMore = React.useCallback(async () => {
    if (loadingMore || !hasMore || loading) return;
    setLoadingMore(true);
    try {
      const r = await api.get(`/posts?limit=${PAGE}&offset=${posts.length}`);
      setPosts((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...r.data.filter((p) => !seen.has(p.id))];
      });
      setHasMore(r.data.length === PAGE);
    } catch {
      toast.error('Could not load more posts');
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, loading, posts.length]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: '400px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const onLike = async (post) => {
    try {
      const r = await api.post(`/posts/${post.id}/like`);
      setPosts((prev) =>
        prev.map((p) => {
          if (p.id !== post.id) return p;
          const likes = new Set(p.likes || []);
          if (r.data.liked) likes.add(user.id); else likes.delete(user.id);
          return { ...p, likes: Array.from(likes) };
        })
      );
    } catch {}
  };

  const pinnedAdId = React.useMemo(() => {
    const ads = posts.filter((p) => p.is_sponsored);
    if (!ads.length) return null;
    try {
      const stored = sessionStorage.getItem('thirrsty_pinned_ad_id');
      if (stored && ads.some((a) => a.id === stored)) return stored;
      sessionStorage.setItem('thirrsty_pinned_ad_id', ads[0].id);
      return ads[0].id;
    } catch {
      return ads[0].id;
    }
  }, [posts]);

  const orderedPosts = React.useMemo(() => {
    if (!pinnedAdId) return posts;
    const pinnedPost = posts.find((p) => p.id === pinnedAdId);
    if (!pinnedPost) return posts;
    return [pinnedPost, ...posts.filter((p) => p.id !== pinnedAdId)];
  }, [posts, pinnedAdId]);

  return (
    <div className="relative min-h-screen" onTouchStart={onPtrStart} onTouchMove={onPtrMove} onTouchEnd={onPtrEnd}>
      <div
        className="pointer-events-none fixed left-0 right-0 top-0 z-40 flex justify-center transition-opacity"
        style={{ opacity: pullY > 8 || ptrBusy ? 1 : 0, transform: `translateY(${Math.max(pullY, ptrBusy ? 55 : 0)}px)` }}
        data-testid="feed-pull-refresh-indicator"
      >
        <span className="mt-2 flex h-9 w-9 items-center justify-center rounded-full border border-[#F6B24A]/40 bg-[#101015] text-[#F6B24A] shadow-lg">
          <RefreshCw className={`h-4 w-4 ${ptrBusy || pullY >= 55 ? 'th-spin' : ''}`} style={!ptrBusy && pullY < 55 ? { transform: `rotate(${pullY * 4}deg)` } : undefined} />
        </span>
      </div>
      <Seo
        title="Home — Nightlife Timeline | Thirrsty.com"
        description="Your live Thirrsty timeline. See who is live tonight at bars, clubs, lounges, coffee shops, vineyards, and breweries near you."
        path="/"
      />
      <header className="sticky top-0 z-30 bg-[#07070A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3">
          <div>
            <Logo size={96} withText={false} />
            <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-white/55" data-testid="home-time-greeting">
              {getCurrentSlot().label}, {user?.display_name?.split(' ')[0]}
            </p>
          </div>
          <div className="flex flex-col items-end gap-0.5">
            <div className="flex items-center gap-1">
              <PushBellButton />
              <ShareAppDialog
                trigger={
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full text-white/80 hover:bg-white/10 hover:text-[#F6B24A]"
                    aria-label="Share Thirrsty with friends"
                    data-testid="home-share-app-button"
                  >
                    <Share2 className="h-5 w-5" />
                  </Button>
                }
              />
              <Button variant="ghost" onClick={() => nav('/profile/go-live')} className="rounded-full text-red-300 hover:bg-red-500/10" data-testid="home-go-live-button">
                <Radio className="mr-2 h-4 w-4 text-red-400" /> Go Live
              </Button>
            </div>
            <button
              onClick={() => setTourOpen(true)}
              className="flex items-center gap-1.5 rounded-full border border-white/12 bg-white/5 px-3 py-1 text-xs font-semibold text-white/75 transition-colors hover:border-[#F6B24A]/45 hover:text-[#F6B24A]"
              data-testid="home-tutorial-button"
            >
              <GraduationCap className="h-3.5 w-3.5" /> App Tutorial
            </button>
          </div>
        </div>
        <button
          onClick={() => nav('/discover')}
          className="ml-auto mt-2 flex w-full max-w-xl items-center justify-between gap-3 rounded-2xl border border-[#F6B24A]/30 bg-gradient-to-r from-white/[0.03] via-[#F6B24A]/10 to-[#F6B24A]/20 px-4 py-2.5 text-left transition-colors hover:border-[#F6B24A]/60 sm:w-auto"
          data-testid="home-discover-banner"
        >
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-white">Find any spot, anywhere in the world</span>
            <span className="block truncate text-xs text-white/60">Bars · Clubs · Lounges · Breweries · Coffee · Wine · Cigar Bars · Liquor Stores</span>
          </span>
          <span className="flex shrink-0 items-center gap-1 text-xs font-bold text-[#F6B24A]">
            Discover <ChevronRight className="h-3.5 w-3.5" />
          </span>
        </button>
      </header>

      <WelcomeTour forceOpen={tourOpen} onClose={() => setTourOpen(false)} />

      <main className="mx-auto max-w-2xl space-y-4 px-4 py-4">
        <StoryBar stories={stories} onAdd={() => setStoryUploadOpen(true)} onView={setStoryView} />
        <LiveStrip liveList={liveList} />
        <ReplaysRail />
        <Composer user={user} onPosted={(p) => setPosts((prev) => [p, ...prev])} />
        {loading ? (
          <FullPageLoader />
        ) : posts.length === 0 ? (
          <EmptyState title="The night's quiet… for now" body="Be the first to post a vibe check." />
        ) : (
          <div className="space-y-4">
            {orderedPosts.map((p) => (
              <PostCard
                key={p.id} post={p} currentUserId={user?.id}
                isAdmin={user?.role === 'admin'}
                pinned={p.id === pinnedAdId}
                onLike={onLike}
                onComment={(post) => setCommentPost(post)}
                onEdit={(post) => setEditingPost(post)}
                onDelete={(post) => setDeletingPost(post)}
              />
            ))}
            <div ref={sentinelRef} aria-hidden="true" />
            {hasMore ? (
              <div className="flex justify-center pt-1 pb-2">
                <Button
                  variant="outline" onClick={loadMore} disabled={loadingMore}
                  className="rounded-xl border-white/12 bg-white/5 text-white hover:bg-white/10"
                  data-testid="timeline-load-more-button"
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            ) : (
              posts.length > 0 && <p className="pt-1 pb-2 text-center text-xs text-white/40" data-testid="timeline-end-of-feed">You're all caught up 🥂</p>
            )}
          </div>
        )}
      </main>

      <CommentSheet open={!!commentPost} post={commentPost} onClose={() => { setCommentPost(null); load(); }} currentUser={user} />
      {editingPost && (
        <EditPostDialog
          post={editingPost}
          onClose={() => setEditingPost(null)}
          onUpdated={(updated) => setPosts((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)))}
        />
      )}
      <AlertDialog open={!!deletingPost} onOpenChange={(v) => !v && setDeletingPost(null)}>
        <AlertDialogContent className="bg-[#0B0C10] border border-white/10 text-white" data-testid="delete-post-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete this post?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">This can't be undone. The post and its comments will be removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl border-white/12 bg-white/5 text-white hover:bg-white/10" data-testid="delete-post-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-xl bg-red-500 text-white hover:bg-red-600" data-testid="delete-post-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <StoryViewerDialog story={storyView} onClose={() => setStoryView(null)} />
      <StoryUploadDialog open={storyUploadOpen} onOpenChange={setStoryUploadOpen} onCreated={(s) => setStories((prev) => [s, ...prev])} />
    </div>
  );
}
